
import { PlusIcon } from '@heroicons/react/24/outline';

import { IconButton } from '@/components/core/IconButton';
import { useTranslation } from '@/hooks/useTranslation';
import { getInjector, getRootScope } from '@/services/angularjs-bridge';
import {
    DataModelAttribute,
    DataModelAttributeTypeName,
} from '@/types/DataModel';

import { AttributeComponent } from '../AttributeComponent';
import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import GroupTitleComponent from './GroupTitleComponent';

const GroupEntry = ({
    attribute,
    fields,
    fieldPath,
    index,
    isExpanded,
    memberAttributes,
    readonly,
    onAdd,
    onDuplicate,
    onRemove,
    onSetEntry,
    onTitleClick,
}: {
    attribute: DataModelAttribute;
    fields: any[];
    fieldPath: string;
    index: number;
    isExpanded: boolean;
    memberAttributes: any;
    readonly: boolean;
    onAdd: (index: number) => void;
    onDuplicate: (index: number) => void;
    onRemove: (index: number) => void;
    onSetEntry:  (index: number) => void;
    onTitleClick: (index: number) => void;
}) => {
    //TOFIX
    const $angularJSInjector = getInjector();
    const $rootScope = getRootScope($angularJSInjector);

    const DEFAULT_WIDGET_SERVICE = 'DefaultWidgetService';
    const customServiceName = Array.isArray(attribute.params?.renderer)
        ? attribute.params?.renderer.find((params: any) => params?.key === 'service')?.value
        : undefined;
    const service = $rootScope?.getService(customServiceName || DEFAULT_WIDGET_SERVICE);
    const { t } = useTranslation();

    const actionEnabled = (action: string) => {
        return service?.getActions(attribute, fields).includes(action);
    };
    let keyAttributeNames: string[] = [];
    if (
        attribute.typeName === DataModelAttributeTypeName.MultiDimensional &&
        attribute?.keyMembers
    ) {
        keyAttributeNames = attribute?.keyMembers;
    }

    function isGroupOdd() {
        const fullPath = `${fieldPath}.${index}`;
        const depth = fullPath
            .split('.')
            .filter((seg) => /^\d+$/.test(seg)).length;

        return depth % 2 === 0;
    }

    const groupRef = useFocusOnAttribute<HTMLDivElement>(attribute);

    return (
        <div ref={groupRef} tabIndex={-1} className="tw-flex tw-w-full tw-py-2">
            <div className="tw-w-full">
                <GroupTitleComponent
                    fieldPath={fieldPath}
                    index={index}
                    attribute={attribute}
                    onTitleClick={onTitleClick}
                    isExpanded={isExpanded}
                    memberAttributes={memberAttributes}
                    bgClore={isGroupOdd() ? 'tw-bg-white' : 'tw-bg-gray-100'}
                    onRemove={onRemove}
                    onDuplicate={onDuplicate}
                    readonly={readonly}
                    actionEnabled = {actionEnabled}
                    onSetEntry= {onSetEntry}/>

                {/*
                  * Hide the group entry body via CSS to keep the group attributes registered in the form
                  * and the react tree.
                  */}
                <div className={`tw-p-10 tw-rounded-b-sm
                                 ${!isExpanded && 'tw-hidden'}
                                 ${isGroupOdd() ? 'tw-bg-white' : 'tw-bg-gray-100'}`}>
                    {memberAttributes?.map(
                        (att: DataModelAttribute, i: number) => (
                            <div className="tw-mb-10 last:tw-mb-0" key={i}>
                                <AttributeComponent
                                    key={`${index}-${attribute.name}`}
                                    attribute={att}
                                    fieldPath={`${fieldPath}.${index}.${att.name}`}
                                    direction="vertical"
                                    readonly= {readonly}
                                    groupLabelType="GroupEntry"
                                    isKeyAttribute={keyAttributeNames?.includes(
                                        att.name,
                                    )}
                                />
                            </div>
                        ),
                    )}
                </div>
            </div>

            <div
                className='group-icon-action tw-h-[40px] tw-w-0 tw-flex tw-items-center'
            >
                {actionEnabled('addEntry') && <IconButton
                    type="button"
                    icon={PlusIcon}
                    disabled={readonly}
                    title={t('ADD_ENTRY')}
                    onClick={(e) => { e.stopPropagation(); onAdd(index); }}
                />}
            </div>
        </div>
    );
};

export { GroupEntry };
